/**
 * What changed, in the rider's words.
 *
 * Shipped in the bundle rather than fetched, so a note can never describe a build that is not the
 * one running: the release and its description deploy together or not at all.
 *
 * Written for someone who uses the app, not someone who reads the diff. A line earns its place by
 * answering "will this morning feel different", so plumbing, refactors and anything invisible are
 * deliberately absent. Names are too — there are two people here and both know who they are.
 */

export interface ReleaseNote {
	kind: "new" | "fixed";
	text: string;
}

export interface Release {
	/** Service date, `YYYY-MM-DD`. Oldest first in `RELEASES`. */
	date: string;
	version: string;
	build: number;
	/** Three-character web build that first carried this release; current release reads from the bundle. */
	webBuild?: string;
	notes: readonly ReleaseNote[];
}

export interface SwipePoint {
	x: number;
	y: number;
}

const RELEASE_SWIPE_DISTANCE_PX = 44;

/** Move one release at a time, leaving vertical page gestures alone. */
export function releaseSwipeTarget(current: number, count: number, start: SwipePoint, end: SwipePoint): number {
	const bounded = Math.max(0, Math.min(current, count - 1));
	const deltaX = end.x - start.x;
	const deltaY = end.y - start.y;
	if (count <= 1 || Math.abs(deltaX) < RELEASE_SWIPE_DISTANCE_PX || Math.abs(deltaX) <= Math.abs(deltaY)) {
		return bounded;
	}
	return Math.max(0, Math.min(deltaX < 0 ? bounded + 1 : bounded - 1, count - 1));
}

/**
 * Oldest first, so history reads left to right. One entry covers one Pacific calendar day: append
 * each user-visible release that day to the existing entry instead of making the history build-sized.
 *
 * Once the day is over its entry is historical and must not be rewritten.
 */
export const RELEASES: readonly Release[] = [
	{
		date: "2026-09-02",
		version: "0.1.3",
		build: 347,
		webBuild: "21b",
		notes: [
			{
				kind: "fixed",
				text: "The app could put you on a later train part-way through a ride. It stays on the one you are actually on.",
			},
			{
				kind: "fixed",
				text: "Countdowns are whole minutes now, everywhere. The train times underneath them move, so the seconds were never as exact as they looked — and the number stops at zero instead of going negative.",
			},
			{
				kind: "new",
				text: "The next step button is back. Every button that commits to something is now a press and hold: keep your thumb down until it fills.",
			},
			{
				kind: "new",
				text: "You can change which stop you get off at from the card while you are on the train, and your ride home follows it.",
			},
			{
				kind: "new",
				text: "You can ask for a ride, or say you do not need one, from the pickup box. It still offers one on its own.",
			},
			{
				kind: "new",
				text: "The pickup box says when your ride has actually been told, rather than only that the app tried.",
			},
			{ kind: "fixed", text: "The pickup screen would not scroll. There is a map and the train's progress under it now." },
			{ kind: "fixed", text: "The pickup card could count down to a train you had already got off." },
			{ kind: "new", text: "Leave reminders say how long you have got in the title, so you can read one without opening it." },
			{ kind: "fixed", text: "The lock screen card now finishes when you get off the train on the way home." },
		],
	},
	{
		date: "2026-09-03",
		version: "0.1.3",
		build: 347,
		webBuild: "00e",
		notes: [
			{
				kind: "new",
				text: "Notifications now keeps your commute, arrival time, starting point, weekdays and all the morning timing in one place.",
			},
			{
				kind: "new",
				text: "Push reminders now default to leave time, 10 minutes before and 30 minutes before. Platform chimes stay on automatically.",
			},
			{
				kind: "new",
				text: "Reset to default settings shows the standard morning setup without changing your saved alert until you press Save.",
			},
			{ kind: "new", text: "What's new keeps earlier releases one swipe away, with the date and app build on every page." },
			{ kind: "fixed", text: "A 15-minute or 5-minute reminder now says exactly that. Time to Go is reserved for the moment you need to leave." },
			{ kind: "fixed", text: "A train update could queue two spoken reminders seconds apart. Only the reminder for the current plan can speak now." },
			{ kind: "fixed", text: "Your card stays on the train you boarded when live departures briefly disappear or change." },
			{ kind: "new", text: "When your destination is the train's next stop, the card now says Next stop." },
			{ kind: "fixed", text: "Walking between the school buildings no longer makes the app say you have left school." },
			{
				kind: "new",
				text: "Wait at the shuttle stop for half a minute and your ride is told you are there. Pickup confirms when the message arrived.",
			},
			{
				kind: "fixed",
				text: "When BART's arrival time has passed but GPS shows your train is still en route, the card now says where it is stopped or that there is no ETA.",
			},
		],
	},
	{
		date: "2026-09-04",
		version: "0.1.4",
		build: 347,
		webBuild: "9bb",
		notes: [
			{ kind: "fixed", text: "Geofences now notice the Civic Center curb and departures from either home more promptly." },
			{
				kind: "new",
				text: "The driver's screen now keeps the commuter's current journey state under the main card, including how it was known and live train progress.",
			},
			{ kind: "fixed", text: "What's new now opens at the size of today's entry. Earlier release days expand only when you ask for them." },
		],
	},
	{
		date: "2026-09-08",
		version: "0.1.4",
		build: 347,
		webBuild: "342",
		notes: [
			{
				kind: "new",
				text: "Arriving at either house now sets which commute the next morning is, and where the drive starts. Your leave reminders name the station you are actually leaving from, without anyone changing a setting.",
			},
			{
				kind: "new",
				text: "On the driver's screen a finished journey now reads teal, arriving at school and arriving home alike. It turns amber again only when the next leg begins, at the shuttle stop or the next morning at home.",
			},
			{
				kind: "new",
				text: "Saying you do not need a ride now tells the driver's screen so, instead of the pickup quietly disappearing with no reason given.",
			},
			{
				kind: "fixed",
				text: "The commuter's journey on the driver's screen goes grey once it is half an hour old, so a reading left over from a phone that went quiet no longer looks current. Swipe it aside and it stays away until there is something new to say.",
			},
		],
	},
	{
		date: "2026-09-09",
		version: "0.2.1",
		build: 349,
		webBuild: "fa1",
		notes: [
			{
				kind: "fixed",
				text: "A journey nobody finished now ends at midnight instead of sitting on screen the next day, and waking up to yesterday's trip no longer sends a notification about it.",
			},
			{
				kind: "fixed",
				text: "The lock screen used to say NO TRAIN for the last walk once the train you came in on had gone. It now counts down to where you are actually going, with the distance.",
			},
			{
				kind: "fixed",
				text: "A service notice that names your line, your stations or the direction you are travelling now counts as affecting you, even when BART files it against something else.",
			},
			{
				kind: "fixed",
				text: "Coming home, the lock screen counted down to when you should set off for the station while the words beside it named the train. It counts the train in now, the same number your card shows.",
			},
			{
				kind: "fixed",
				text: "Time to spare no longer shrinks while you are still sitting at home. Until you have to leave it says what you will have if you go on time; once you are leaving it counts the real one down.",
			},
			{
				kind: "fixed",
				text: "I'm here no longer buzzes the commuter when the car is nowhere near the station.",
			},
			{
				kind: "fixed",
				text: "The lock screen card stops flickering. Reopening the app no longer takes down the pickup card for a few seconds, and a card that has no train and no journey to describe is not raised at all.",
			},
			{
				kind: "fixed",
				text: "Opening the app at home in the evening no longer announces that the commuter is at school. Standing in a known house now contradicts that claim instead of being ignored.",
			},
			{
				kind: "new",
				text: "A faint arrow appears in the top right once you have scrolled a screen or so down. Tap it to get back to the top.",
			},
			{
				kind: "new",
				text: "Before you leave, the margin reads \"5 minutes to spare at departure\" — the room the plan sets aside for you. Once you are going it counts the real one down as time to spare.",
			},
		],
	},
	{
		date: "2026-09-10",
		version: "0.2.1",
		build: 349,
		webBuild: "f08",
		notes: [
			{
				kind: "fixed",
				text: "Pickup Mode now keeps What's new on screen instead of hiding it with the ordinary journey details.",
			},
			{
				kind: "fixed",
				text: "While you wait to leave for a pickup, the card shows the five minutes the plan leaves you to spare at departure. Once you set off, it switches to the live margin.",
			},
			{
				kind: "fixed",
				text: "Pickup Mode now shows the commuter's usual live journey update, current ride schedule and BART map. The smaller list of possible trains has been removed.",
			},
		],
	},
	{
		date: "2026-09-11",
		version: "0.2.2",
		build: 350,
		webBuild: "234",
		notes: [
			{
				kind: "new",
				text: "Liquid glass is now the default in both dark and light modes. Settings remembers your appearance choices, including switching glass off.",
			},
			{
				kind: "fixed",
				text: "Liquid glass now has clearer translucent surfaces and reflected edges. The first preview was too subtle to distinguish from the normal appearance.",
			},
			{
				kind: "fixed",
				text: "The commuter's live status no longer steps back when reports arrive out of order. On the train, their destination and arrival countdown are now one line.",
			},
			{
				kind: "fixed",
				text: "Delayed trains keep their last known realtime departure after leaving, instead of reverting to the timetable and exaggerating how long ago they left.",
			},
			{
				kind: "new",
				text: "TestFlight feedback has a shared inbox in Admin, with original screenshots and review history. The new shell can open feedback notifications directly to their submission.",
			},
			{
				kind: "new",
				text: "Every Admin section can be folded away. Beta feedback starts collapsed; a direct submission link opens it for you.",
			},
		],
	},
	{
		date: "2026-09-12",
		version: "0.2.2",
		build: 350,
		webBuild: "ed3",
		notes: [
			{ kind: "new", text: "Pickup Mode has Location and Pickup street maps, with phone position ages, accuracy, person focus and follow. Civic Center spots can be proposed privately; navigation and commuter notifications are unchanged." },
			{ kind: "new", text: "The Pickup status block stays below the train cards when you swipe. The BART System Map has been removed; Train Tracker remains." },
			{ kind: "fixed", text: "Pickup Planning now includes private map previews without starting a pickup. Spacing between commuter status and BART alerts matches the main screen." },
		],
	},
	{
		date: "2026-09-13",
		version: "0.2.2",
		build: 350,
		webBuild: "ae2",
		notes: [
			{ kind: "new", text: "Maps: Location and Pickup views show both people during a shared pickup, with one current Civic Center spot either person can set or clear. Planning has private map previews. Maps follow Light/Dark appearance; one finger scrolls the page and two fingers pan or pinch." },
			{ kind: "new", text: "Shared schedule: Set Civic Center, None or Glen Park for each day's AM and PM in Alerts. Both people share the same repeating week, while arrival times and buffers stay personal. None silences commute notifications and keeps unscheduled mornings in Planning." },
		],
	},
	{
		date: "2026-09-14",
		version: "0.2.2",
		build: 350,
		webBuild: "82e",
		notes: [
			{ kind: "fixed", text: "Feedback disposition dropdowns keep their options readable in both dark and light mode." },
			{ kind: "new", text: "Distant morning plans use cached or typical drive times. Pickup browsing shares one recent traffic estimate across trains; active driving and imminent committed pickups keep frequent traffic updates." },
			{ kind: "fixed", text: "Planned time to spare includes both timing buffers: 2 minutes to get out the door plus 5 at the station shows 7 minutes. Your leave time is unchanged." },
			{ kind: "fixed", text: "The ride-home notification saying the commuter is at school now requires a recent location inside campus. Opening the app or a midnight journey reset is not enough." },
			{ kind: "fixed", text: "The commuter status retries stalled requests and refreshes when the app regains focus or connectivity. Late responses cannot overwrite a newer refresh. iPhone background recovery is still being checked." },
			{ kind: "fixed", text: "Beta feedback can be marked Open, Future, Won't fix, Obsolete, Fixed or Snoozed without writing a note. Open stays in Inbox; the other choices go to Archive with history intact." },
			{ kind: "fixed", text: "Pickup Planning's header follows the scheduled pickup station. Glen Park now has a Bosworth Street pickup rectangle and a tighter station view. Both people can set or clear its shared spot." },
			{ kind: "fixed", text: "Open maps request your phone's location even outside a commute. Both phones' last-known positions stay visible for up to seven days, clearly dated when old; your uploaded GPS fills a missing page fix." },
			{ kind: "fixed", text: "Dark maps keep a dark background while street tiles load, without tile fading. The reported iPhone tile line still needs on-device confirmation." },
			{ kind: "fixed", text: "Pickup Planning says 2 hours 22 minutes instead of 142 minutes, with the remaining minutes in smaller text." },
			{ kind: "new", text: "Commuter status shows GPS age and accuracy separately from train confirmation. Good GPS at MacArthur confirms the train as Observed, with the sighting time, even if GPS disappears again." },
			{ kind: "fixed", text: "If your phone stops reporting mid-journey, the Live Activity no longer picks a later train for you and starts urging you to hurry. It keeps counting down to a train you already chose, and otherwise greys out until your phone checks in again." },
		],
	},
	{
		date: "2026-09-15",
		version: "0.2.3",
		build: 351,
		webBuild: "985",
		notes: [
			{ kind: "fixed", text: "How this adds up stays at the top of the trip breakdown, so you can expand and hide it from the same place." },
			{ kind: "fixed", text: "Commuter status uses a clear home GPS observation instead of saying At school after the app switches back to the completed morning journey. It names the home actually seen and keeps the GPS observation's age visible." },
			{ kind: "fixed", text: "A phone returning to Planning no longer replaces the commuter's last reported location with At home. Older status keeps its original age, and Planning no longer names a home without a reported home state." },
			{ kind: "fixed", text: "Return Home now names the scheduled station and its matching home in the header: Glen Park and Twin Peaks, or Civic Center and Richmond Dist, matching the train card." },
			{ kind: "fixed", text: "\"Off the train\" now says Inferred when it comes from the train being due rather than from where the phone is. Only an arrival actually seen at the station is called Observed." },
			{ kind: "fixed", text: "The Live Activity countdown can go below zero instead of staying at 0 min, so you can see when the pinned train's time has already passed. Requires app update 0.2.3 (351)." },
		],
	},
	{
		date: "2026-09-16",
		version: "0.2.3",
		build: 351,
		webBuild: "0eb",
		notes: [
			{ kind: "fixed", text: "Switching journeys no longer carries the previous leg's arrival into the new one. Coming home could incorrectly announce an arrival at school." },
			{ kind: "fixed", text: "Pickup Mode keeps the Rockridge-to-pickup-station header above the card, without the direction switch." },
			{ kind: "new", text: "The rider's Pickup heading has a refresh button. Old driver readings keep their age, and failed refreshes are shown." },
			{ kind: "fixed", text: "Once the rider is off the train, Got him is available beside I'm here. You can finish the pickup after driving away without first claiming you are still at the curb." },
			{ kind: "fixed", text: "Waking the app on a new day no longer reports yesterday's journey first. Opening it in the car could announce that the commuter had already arrived at school." },
			{ kind: "new", text: "Leave reminders add \"Open GoTime to get live updates\" on a day the app has not been opened yet, because a shut app can still be reminded but cannot be followed." },
			{ kind: "fixed", text: "The arrival announcement is held back when a fresh, accurate position is plainly nowhere near school. Arriving with the phone in a pocket still announces as before." },
		],
	},
	{
		date: "2026-09-17",
		version: "0.2.4",
		build: 353,
		webBuild: "307",
		notes: [
			{ kind: "new", text: "GoTime is on the Apple Watch. Install it from the Watch app on your phone. Three screens, swiped up and down: what to do now, the train deck, and the afternoon lift home." },
			{ kind: "new", text: "The watch shows the same countdown as the Dynamic Island, with room for the line the island has no width for — your next stop and how long until you get off." },
			{ kind: "new", text: "You can confirm which train you are on from your wrist. Swiping through the deck changes nothing; it only counts once you press. The card says Asking until your phone confirms it, so a brush against your sleeve cannot move your train." },
			{ kind: "new", text: "If your phone is shut or out of reach, the watch says how old what it is showing is, then fetches a fresh answer on its own." },
			{ kind: "new", text: "The afternoon screen names the agreed pickup spot, which until now only the map could show." },
			{ kind: "fixed", text: "Opening the app well before your first reminder no longer leaves it without your location for the rest of the morning. It was still pricing the drive from the house while you were nearly at the station, so trains you walked onto were shown as missed." },
			{ kind: "fixed", text: "A shared schedule that fails to refresh no longer reads as \"no commute today\". The last schedule it loaded stays in force, so the card cannot drop to NO TRAIN mid-drive." },
			{ kind: "new", text: "Google Maps sits under the Waze button on the card. Same curb, either app, one tap each." },
			{ kind: "fixed", text: "Pickup planning cards say whether Eben could actually be on each train from school — Next, Tight, Option, Will miss, Missed — instead of labelling every card Private." },
		],
	},
	{
		date: "2026-09-24",
		version: "0.2.4",
		build: 353,
		webBuild: "709",
		notes: [
			{ kind: "new", text: "The driver can acknowledge each step of the rider's pickup progress, and say On Time or Running Late — shown right on the rider's card, with when it was last said." },
			{ kind: "fixed", text: "A good MacArthur GPS sighting now confirms the rider's train as Observed even when BART uses a longer station name." },
			{ kind: "new", text: "After the morning drop-off, the driver's main view includes a simple Eben-only location map with automatic follow." },
			{ kind: "fixed", text: "At the platform and on the train, Eben's map marker follows his journey instead of underground GPS that could repeatedly place him near the wrong part of Market Street. A faint dashed boundary still shows what uncertainty the phone reported." },
			{ kind: "fixed", text: "Opening a new morning starts on the target train instead of making the rider swipe away from the next departure." },
			{ kind: "fixed", text: "The Dynamic Island now ends when the train ride ends instead of tracking the approximate shuttle time to school. The last leg remains visible in GoTime." },
			{ kind: "fixed", text: "The estimated map marker and follow control now also cover the ride home, not just the morning — the driver's live pickup map no longer shows raw underground GPS once Eben is on the train." },
			{ kind: "fixed", text: "The map no longer freezes at a station for minutes straight after a connectivity gap; it now shows the honest last-known signal instead of a confident, stuck dot." },
			{ kind: "fixed", text: "Eben's status no longer gets stuck on an earlier step of his journey after the app has been in the background for a while — it catches back up from the stations he has actually passed." },
			{ kind: "fixed", text: "Re-selecting the same train on the pickup deck no longer triggers a second \"leave now\" push a few minutes after the first." },
		],
	},
	{
		date: "2026-09-25",
		version: "0.2.4",
		build: 353,
		webBuild: "987",
		notes: [
			{ kind: "new", text: "The live train card shows how many minutes early or late BART is running compared with its schedule." },
			{ kind: "new", text: "The driver's morning map now draws the BART route, keeps the next stop visible and outlines the College Prep campus." },
			{ kind: "fixed", text: "The rider marker has a stronger blue fill, dark outline and gentle pulse, while an estimated position stays lighter and dashed." },
			{ kind: "fixed", text: "Center and Follow are separate map controls. You can zoom out and keep following, or recenter once without changing the follow setting." },
			{ kind: "fixed", text: "After an accurate MacArthur train sighting, the map returns from timetable interpolation to the rider's live GPS position." },
			{ kind: "fixed", text: "A train first declared by the rider and later confirmed by GPS says Observed + Declared instead of hiding one of those facts." },
			{ kind: "new", text: "SFO can be set as a one-off afternoon destination in the shared schedule, with the same arrival cards as Civic Center or Glen Park." },
			{ kind: "fixed", text: "A second GPS checkpoint at Balboa Park now hands the map back to live tracking on the ride home, then releases it again once the train goes back underground — covering the SFO leg and steadying Civic Center and Glen Park too." },
			{ kind: "fixed", text: "Before boarding, the ride-home card now offers the same destination picker as it does once aboard, still defaulting to whatever the shared schedule names for today." },
			{ kind: "fixed", text: "The train list below the card now leads with the arrival time for the destination you picked, with the other stations shown as secondary detail, instead of always leading with Civic Center." },
			{ kind: "fixed", text: "The destination picker now sits strictly below the train time instead of crowding onto the same row, and a two-digit hour like 11:12 am no longer bleeds past its box." },
			{ kind: "new", text: "Show more trains: a card at the end of the deck and a link below the list widen today's search by an hour, or five more trains, whichever the schedule has fewer of." },
			{ kind: "fixed", text: "Pickup and follow maps now show the BART line and the College Prep campus outline on every view, not only while a live journey was being tracked." },
			{ kind: "fixed", text: "When the rider reaches a one-off destination such as SFO, the driver's status now changes to Off the train with the arrival notification instead of staying one step behind." },
		],
	},
	{
		date: "2026-09-30",
		version: "0.2.4",
		build: 353,
		webBuild: "498",
		notes: [
			{ kind: "fixed", text: "Being offline is obvious now: the whole bar along the bottom takes on a faint red tint until the connection is back." },
			{ kind: "fixed", text: "Panning any map with two fingers now turns Follow off, so it stays where you put it. Pinching to zoom keeps following." },
		],
	},
	{
		date: "2026-10-02",
		version: "0.2.4",
		build: 353,
		webBuild: "6f2",
		notes: [
			{ kind: "fixed", text: "The morning rider map can open as soon as the driver reaches the shared boarding station, without waiting for the rider's status to catch up." },
			{ kind: "fixed", text: "At the station, and after a timetable-only arrival, the rider's map marker stays at the expected station rather than jumping to a misleading underground GPS fix. The marker is dashed and labelled as estimated, not observed." },
			{ kind: "new", text: "If the rider's status cannot refresh, the monitor now says so and retries automatically, keeping the last reading and its age visible." },
			{ kind: "fixed", text: "The pickup card says Selected train before boarding. Swiping to a train selects it; it does not confirm that the rider is aboard." },
		],
	},
	{
		date: "2026-10-08",
		version: "0.2.4",
		build: 353,
		webBuild: "cd8",
		notes: [
			{ kind: "fixed", text: "Walking out of the station now counts as off the train, even before the timetable says the train is in. Fixes at the exit stairs and the curb no longer keep the ride home stuck on On train." },
			{ kind: "fixed", text: "Marking the pickup done now ends the ride on the driver's status too." },
			{ kind: "new", text: "The driver's pickup card drops the small step row. The rider status panel below it is now the one readout, and it uses the pickup's own record when that is further along." },
			{ kind: "new", text: "The ride-home maps show the train's next stop, like the morning follow map." },
		],
	},
	{
		date: "2026-10-14",
		version: "0.2.4",
		build: 353,
		webBuild: "6b9",
		notes: [
			{ kind: "fixed", text: "Changing trains updates the followed train and its timings together. If the new train's timings are missing, the map no longer carries on following the old one." },
			{ kind: "fixed", text: "The rider's estimated position and next stop advance together. The estimate stops at the chosen destination, and an overdue arrival time is no longer shown as arriving in zero minutes." },
			{ kind: "new", text: "Every map shares the same BART routes and station circles, following the real track alignment including the Transbay Tube." },
			{ kind: "fixed", text: "Tap a pickup spot to set it immediately, without a separate Set button. The map opens with the current spot highlighted, or Hyde North / Bosworth as an unshared default." },
		],
	},
	{
		date: "2026-10-16",
		version: "0.2.4",
		build: 353,
		webBuild: "6c4",
		notes: [
			{ kind: "fixed", text: "BART routes stay joined through station dots instead of kinking through older station coordinates. Civic Center, Glen Park and Rockridge have larger dots so the key stops stand out on every map." },
		],
	},
				{
					date: "2026-10-19",
					version: "0.2.4",
					build: 353,
					notes: [
						{ kind: "fixed", text: "Pickup messages no longer push the screen down with a separate \"Saving…\" line, and the driver now sees \"Sent. Rider hasn't seen it yet.\" instead of \"Saved. Waiting for rider's screen.\"" },
					],
				},
			];
